import { useState, useEffect, Fragment } from 'react';
import { useConfirm } from 'material-ui-confirm';
import { toast } from 'react-toastify';
import { formatDateTime } from '../../utils/dateUtils';

import {
  Box,
  Button,
  IconButton,
  TextField,
  Typography,
  Tooltip,
  Divider,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Collapse,
} from '@mui/material';

import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import SmsIcon from '@mui/icons-material/Sms';
import HomeIcon from '@mui/icons-material/Home';
import ScheduleIcon from '@mui/icons-material/Schedule';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import GavelIcon from '@mui/icons-material/Gavel';
import BlockIcon from '@mui/icons-material/Block';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import AddIcon from '@mui/icons-material/Add';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import messages from '../../static/messages';
import proposalsServices from '../../services/proposalsServices';
import { button } from '../../static/theme-styles';

// ── Constants ──
const TOUCH_TYPES = [
  { value: 'Call', label: 'Call', color: '#1976d2' },
  { value: 'Email', label: 'Email', color: '#7b1fa2' },
  { value: 'Text', label: 'Text', color: '#388e3c' },
  { value: 'Visit', label: 'Visit', color: '#f57c00' },
  { value: 'Scheduled', label: 'Scheduled', color: '#0097a7' },
  { value: 'Waiting', label: 'Waiting', color: '#fbc02d' },
  { value: 'Competitive', label: 'Competitive', color: '#d32f2f' },
  { value: 'Lost', label: 'Lost', color: '#e91e63' },
  { value: 'Other', label: 'Other', color: '#795548' },
];

const TOUCH_ICONS = {
  Call: PhoneIcon,
  Email: EmailIcon,
  Text: SmsIcon,
  Visit: HomeIcon,
  Scheduled: ScheduleIcon,
  Waiting: HourglassEmptyIcon,
  Competitive: GavelIcon,
  Lost: BlockIcon,
  Other: MoreHorizIcon,
};

const TOUCH_OUTCOMES = [
  { value: 'Positive', color: '#4caf50' },
  { value: 'Neutral', color: '#9e9e9e' },
  { value: 'Negative', color: '#f44336' },
  { value: 'No Answer', color: '#ff9800' },
  { value: 'Callback', color: '#2196f3' },
  { value: 'Follow Up', color: '#673ab7' },
];

// Background colors for the Contact History cell on the proposals list
const CELL_COLORS = [
  { value: '#2196f3', label: 'Blue' },
  { value: '#4caf50', label: 'Green' },
  { value: '#ffeb3b', label: 'Yellow' },
  { value: '#f44336', label: 'Red' },
  { value: '#ff9800', label: 'Orange' },
  { value: '#00bcd4', label: 'Light Blue' },
];

const getTouchMeta = (type) => TOUCH_TYPES.find((t) => t.value === type) || TOUCH_TYPES[8];

const safeFormatDate = (date) => {
  try {
    if (!date) return '—';
    return formatDateTime(date);
  } catch (e) {
    return String(date || '').substring(0, 16);
  }
};

const ProposalTouches = ({ proposalId, contactHistoryColor = null }) => {
  const [touches, setTouches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [touchType, setTouchType] = useState('Call');
  const [outcome, setOutcome] = useState('');
  const [note, setNote] = useState('');
  const [nextFollowUpAt, setNextFollowUpAt] = useState('');
  const [cellColor, setCellColor] = useState(contactHistoryColor);

  useEffect(() => { setCellColor(contactHistoryColor); }, [contactHistoryColor]);

  const handleColorChange = async (color) => {
    const prev = cellColor;
    setCellColor(color);
    try {
      await proposalsServices.update(proposalId, { contactHistoryColor: color });
      toast.success(color ? 'Cell color updated' : 'Cell color cleared');
    } catch (e) {
      setCellColor(prev);
      toast.error(e?.response?.data?.message || 'Failed to save color');
    }
  };

  const confirm = useConfirm();

  let currentUser = null;
  try {
    currentUser = JSON.parse(localStorage.getItem('user'));
  } catch (e) {
    // ignore
  }
  const isAdmin = currentUser?.role === 'superadmin' || currentUser?.role === 'admin';

  const fetchTouches = async () => {
    try {
      const response = await proposalsServices.getTouches(proposalId);
      if (response.status === 200) {
        setTouches(Array.isArray(response.data) ? response.data : []);
      }
    } catch (e) {
      console.log('Touches not available:', e?.response?.status);
      setTouches([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (proposalId) {
      fetchTouches();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proposalId]);

  const resetForm = () => {
    setTouchType('Call');
    setOutcome('');
    setNote('');
    setNextFollowUpAt('');
    setShowForm(false);
  };

  const handleAdd = async () => {
    setSubmitting(true);
    try {
      const data = {
        touchType: touchType,
        contactedAt: new Date().toISOString(),
      };
      if (outcome) data.outcome = outcome;
      if (note.trim()) data.note = note.trim();
      if (nextFollowUpAt) data.nextFollowUpAt = new Date(nextFollowUpAt).toISOString();

      const response = await proposalsServices.createTouch(proposalId, data);
      if (response.status === 201 || response.status === 200) {
        toast.success('Contact logged successfully');
        resetForm();
        fetchTouches();
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Failed to log contact');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = (touchId) => {
    confirm({
      title: 'Delete Contact Record',
      description: 'Are you sure you want to delete this contact record?',
    })
      .then(async () => {
        try {
          await proposalsServices.deleteTouch(proposalId, touchId);
          toast.success('Contact record deleted');
          fetchTouches();
        } catch (e) {
          toast.error(messages.errors.error_try_again);
        }
      })
      .catch(() => {});
  };

  // Loading state
  if (loading) {
    return (
      <Box>
        <Typography sx={{ fontWeight: 600, mb: 1 }}>Contact History:</Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={20} />
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
          Contact History
          {touches.length > 0 && (
            <Chip label={touches.length} size="small" sx={{ height: 20, fontSize: '0.75rem' }} />
          )}
        </Typography>
        <Button
          variant="contained"
          size="small"
          startIcon={showForm ? <ExpandLessIcon /> : <AddIcon />}
          onClick={() => setShowForm(!showForm)}
          sx={{ ...button('secondary'), minWidth: 'auto', fontSize: '0.75rem', py: 0.5 }}
        >
          {showForm ? 'Cancel' : 'Log Contact'}
        </Button>
      </Box>

      {/* Cell background color on the proposals list */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <Typography variant="caption" sx={{ color: 'text.secondary', mr: 0.5 }}>
          Cell color:
        </Typography>
        {CELL_COLORS.map((c) => (
          <Tooltip key={c.value} title={c.label}>
            <Box
              onClick={() => handleColorChange(c.value)}
              sx={{
                width: 22, height: 22, borderRadius: '50%', cursor: 'pointer',
                backgroundColor: c.value,
                border: cellColor === c.value ? '3px solid #333' : '2px solid #fff',
                boxShadow: '0 0 0 1px rgba(0,0,0,0.25)',
                transition: 'transform 0.1s',
                '&:hover': { transform: 'scale(1.15)' },
              }}
            />
          </Tooltip>
        ))}
        <Tooltip title="No color">
          <Box
            onClick={() => handleColorChange(null)}
            sx={{
              width: 22, height: 22, borderRadius: '50%', cursor: 'pointer',
              backgroundColor: '#fff',
              border: !cellColor ? '3px solid #333' : '2px solid #fff',
              boxShadow: '0 0 0 1px rgba(0,0,0,0.25)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 12, color: '#999', lineHeight: 1,
              transition: 'transform 0.1s',
              '&:hover': { transform: 'scale(1.15)' },
            }}
          >
            ✕
          </Box>
        </Tooltip>
      </Box>

      {/* Add Touch Form */}
      <Collapse in={showForm}>
        <Box sx={{ p: 2, mb: 2, border: '1px solid #e0e0e0', borderRadius: 2, backgroundColor: '#fafafa' }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Type</InputLabel>
              <Select value={touchType} onChange={(e) => setTouchType(e.target.value)} label="Type">
                {TOUCH_TYPES.map((t) => (
                  <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Outcome</InputLabel>
              <Select value={outcome} onChange={(e) => setOutcome(e.target.value)} label="Outcome">
                <MenuItem value=""><em>None</em></MenuItem>
                {TOUCH_OUTCOMES.map((o) => (
                  <MenuItem key={o.value} value={o.value}>{o.value}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              size="small"
              type="datetime-local"
              label="Next Follow-Up"
              value={nextFollowUpAt}
              onChange={(e) => setNextFollowUpAt(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 200 }}
            />
          </Box>

          <TextField
            fullWidth size="small" label="Note (optional)"
            multiline rows={2} value={note}
            onChange={(e) => setNote(e.target.value)}
            sx={{ mb: 2 }}
          />

          <Button
            variant="contained" size="small"
            onClick={handleAdd} disabled={submitting}
            sx={{ ...button('secondary') }}
          >
            {submitting ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Save Contact'}
          </Button>
        </Box>
      </Collapse>

      {/* Touches List */}
      {touches.length === 0 ? (
        <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic', py: 1 }}>
          No contacts logged yet. Click "Log Contact" to record a call, email, or visit.
        </Typography>
      ) : (
        touches.map((touch) => {
          const meta = getTouchMeta(touch.touchType);
          const IconComp = TOUCH_ICONS[touch.touchType] || MoreHorizIcon;
          const outcomeMeta = TOUCH_OUTCOMES.find((o) => o.value === touch.outcome);

          return (
            <Fragment key={touch.id}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', mt: 1.5, gap: 1.5 }}>
                <Box sx={{
                  width: 32, height: 32, borderRadius: '50%',
                  backgroundColor: meta.color + '18', color: meta.color,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0, mt: 0.25,
                }}>
                  <IconComp fontSize="small" />
                </Box>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Chip label={touch.touchType} size="small" sx={{
                      height: 22, fontSize: '0.7rem', fontWeight: 600,
                      backgroundColor: meta.color + '18', color: meta.color,
                    }} />
                    {touch.outcome && outcomeMeta && (
                      <Chip label={touch.outcome} size="small" sx={{
                        height: 22, fontSize: '0.7rem',
                        backgroundColor: outcomeMeta.color + '18', color: outcomeMeta.color,
                      }} />
                    )}
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      {safeFormatDate(touch.contactedAt)}
                      {touch.createdBy && (' by ' + (touch.createdBy.fullName || touch.createdBy.firstName || 'User'))}
                    </Typography>
                  </Box>

                  {touch.note && (
                    <Typography variant="body2" sx={{ mt: 0.5 }}>{touch.note}</Typography>
                  )}

                  {touch.nextFollowUpAt && (
                    <Typography variant="caption" sx={{ color: '#1976d2', mt: 0.5, display: 'block' }}>
                      Next follow-up: {safeFormatDate(touch.nextFollowUpAt)}
                    </Typography>
                  )}
                </Box>

                {isAdmin && (
                  <Tooltip title="Delete">
                    <IconButton size="small" onClick={() => handleDelete(touch.id)}>
                      <DeleteForeverIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
              <Divider sx={{ mt: 1.5 }} />
            </Fragment>
          );
        })
      )}
    </Box>
  );
};

export default ProposalTouches;
